import { Namespace } from 'enums-lib';
import { IMergeFile, IRemoteFileInfo, WorkflowMessage } from 'interfaces-lib';
import { WorkflowTask } from 'workflow-task-service-lib';
import { IFileEditorController } from '../file-list-view/file-list-view.component';
import { IFileToPathConverter } from '../interfaces/ifiletopathconverter';
import { IS3File } from '../merge-component/merge-controller.service';
import { buildSalesforceContactDocumentParameters } from '../utils/salesforce-contact-document-helpers';

export class EditFileTask extends WorkflowTask {
  readonly startParams: any;
  private readonly srcFile: IMergeFile | IS3File;
  private readonly fileModifiedController: IFileEditorController;
  private readonly namespace: string;
  constructor(
    namespace: string,
    srcFile: IMergeFile | IS3File,
    fileToPathConverter: IFileToPathConverter,
    fileModifiedController: IFileEditorController,
  ) {
    super(EditFileTask.isIFileS3(srcFile) ? 'EDIT_DOCUMENT_S3' : 'EDIT_DOCUMENT');
    this.srcFile = srcFile;
    this.fileModifiedController = fileModifiedController;
    this.startParams = EditFileTask.isIFileS3(this.srcFile)
      ? this.buildStartParamsS3()
      : this.buildStartParams(namespace, fileToPathConverter, this.srcFile as IMergeFile);
    this.startParams.SOURCE_APPLICATION = "DOC_CLIENT";
    this.namespace = namespace;
  }

  override getStartParams(): any {
    return this.startParams;
  }

  override receive(message: WorkflowMessage): boolean {
    if (message.type === 'ErrorEvent') {
      return this.handleError(message);
    }

    if (message.type === 'LastModifiedResponse') {
      return this.isModifiedFile(message.message);
    }

    // editorHost, fileName, lastModified, path ('/document/kf_xxxxx.docx')
    if (message.type === 'SendToEditorResponse') {
      return this.openFile(<IRemoteFileInfo>message.message);
    }

    // editorHost, fileName,  path (['people', '<guid>', 'documents', <guid>])
    if (message.type === 'SavedToEntity') {
      return this.savedFile(<IRemoteFileInfo>message.message);
    }

    console.debug(`Message '${message.type}' not handled.`);

    return false;
  }

  private static isIFileS3(obj: any): obj is IS3File {
    return 'key' in obj;
  }

  private buildStartParamsS3(): any {
    return {
      RESTORE_STATE: {
        key: (<IS3File>this.srcFile).key,
      },
    };
  }

  private buildStartParams(namespace: string, fileToPathConverter: IFileToPathConverter, file: IMergeFile): any {
    const path = fileToPathConverter.convert(file);
    const parameters = this.buildDatasourceParameters(namespace, file);
    const datasource: any = {
      namespace: namespace,
      path,
    };

    if (parameters) {
      datasource.parameters = parameters;
    }

    return { datasource };
  }

  // parameters needed for the backend to identify ContactDocument records
  private buildDatasourceParameters(namespace: string, file: IMergeFile): Record<string, any> | undefined {
    return buildSalesforceContactDocumentParameters(namespace, file);
  }

  private openFile(fileInfo: IRemoteFileInfo): boolean {
    const documentUrl = `ms-word:ofe|u|${fileInfo.editorHost}${fileInfo.path}`;
    const documentWindow = this.openWord(documentUrl);

    if (!documentWindow) {
      return this.handleError('Could not open Word.');
    }

    this.fileModifiedController.fileOpened(this.srcFile, fileInfo);
    return false;
  }

  private isModifiedFile(modifiedInfo: any): boolean {
    if (!modifiedInfo.success) {
      // TODO: raise an error?
      return false;
    }

    if (modifiedInfo.modified) {
      this.fileModifiedController.fileModified(this.srcFile, modifiedInfo);
    }

    return false;
  }

  private savedFile(fileInfo: IRemoteFileInfo): boolean {
    this.fileModifiedController.fileSaved(this.srcFile, fileInfo);
    return false;
  }

  private handleError(error: any): boolean {
    this.fileModifiedController.fileError(error, 'action-edit', this.srcFile);
    return true;
  }

  protected setTopLocationHref(url: string): void {
    window.top.location.href = url;
  }

  private openWord(documentUrl) {
    if (this.namespace === Namespace.salesforce) {
      this.setTopLocationHref(documentUrl);
      return documentUrl;
    } else {
      return window.open(documentUrl, '_self');
    }
  }
}
